import { createTheme } from '@mui/material/styles';

import { customShadows } from './custom-shadows';

/**
 * GitHub Primer "Dark dimmed" (soft dark) theme. Muted slate canvas, hairline
 * borders, a green primary CTA, blue accents, compact controls. Close enough to
 * stock MUI that it still drops cleanly into an existing Material UI app.
 */
const CANVAS = '#22272e'; // canvas.default (page background, inputs)
const INSET = '#1c2128'; // canvas.inset (deeper wells)
const PAPER = '#2d333b'; // canvas.overlay (cards, app bar, menus)
const BORDER = '#444c56'; // border.default
const BORDER_MUTED = '#373e47'; // border.muted
const FG = '#adbac7'; // fg.default
const FG_MUTED = '#768390'; // fg.muted
const ACCENT = '#539bf5'; // accent.fg
const GREEN = '#347d39'; // success button emphasis
const GREEN_HOVER = '#46954a';

export const adminTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: GREEN, dark: GREEN_HOVER, contrastText: '#ffffff' },

    secondary: { main: ACCENT },
    info: { main: ACCENT },
    success: { main: '#57ab5a' },
    warning: { main: '#c69026' },
    error: { main: '#e5534b' },
    background: { default: CANVAS, paper: PAPER },
    divider: BORDER,
    text: { primary: FG, secondary: FG_MUTED },
    action: {
      selected: 'rgba(83, 155, 245, 0.15)',
      hover: 'rgba(144, 157, 171, 0.1)',
    },
  },
  shape: { borderRadius: 6 },
  customShadows: customShadows('dark'),
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif',
    fontSize: 13,
    h6: { fontWeight: 600, fontSize: '1rem', letterSpacing: 0 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 500 },
    caption: { color: FG_MUTED },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': { colorScheme: 'dark' },
        body: { backgroundColor: CANVAS, color: FG },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(110, 118, 129, 0.4)',
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(110, 118, 129, 0.6)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
        disableElevation: true,
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
          fontWeight: 500,
          minHeight: 28,
          padding: '3px 12px',
        },
        outlined: {
          color: FG,
          borderColor: 'rgba(205,217,229,0.1)',
          backgroundColor: BORDER_MUTED,
          '&:hover': {
            backgroundColor: BORDER,
            borderColor: 'rgba(205,217,229,0.15)',
          },
        },
        contained: { border: '1px solid rgba(205,217,229,0.1)' },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: { padding: '5px 10px', fontSize: 13 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { backgroundColor: CANVAS, borderRadius: 6 },
        notchedOutline: { borderColor: BORDER },
        input: { padding: '5px 10px' },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small', fullWidth: true },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
    },
    // Date pickers render their own field instead of TextField, so mirror the
    // TextField/OutlinedInput styling above for them. They keep MUI's 40px
    // small height so the floating label stays vertically centred.
    MuiPickersTextField: {
      defaultProps: { size: 'small', fullWidth: true },
    },
    MuiPickersInputBase: {
      styleOverrides: { root: { fontSize: 13, minHeight: 40 } },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: { backgroundColor: CANVAS, borderRadius: 6 },
        notchedOutline: { borderColor: BORDER },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: 13 } },
    },
    MuiMenuItem: {
      styleOverrides: { root: { fontSize: 13, minHeight: 32 } },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: 52, '@media (min-width:600px)': { minHeight: 52 } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: BORDER },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          borderRadius: 6,
          borderColor: BORDER,
          transition: 'border-color .15s ease, box-shadow .15s ease',
          '&:hover': {
            borderColor: '#545d68',
            boxShadow: '0 1px 3px rgba(1,4,9,0.5)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, borderRadius: 999 },
        sizeSmall: { height: 20, fontSize: 12 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, minHeight: 40 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: 12,
          borderRadius: 6,
          backgroundColor: INSET,
          border: `1px solid ${BORDER}`,
          color: FG,
          paddingInline: 10,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: { borderColor: BORDER, '&:before': { display: 'none' } },
      },
    },
  },
});
